import { z } from 'zod';
import type { ActionSchema } from '@wix/cli-astro-definitions';
import { ExtensionDataClient } from '@wix/extensions-data-client';
import type { BackOfficeExtensionContainer } from '@wix/extensions-data-client';
import { getDashboardMenuPluginExtensionsDir } from '@wix/cli-astro-definitions';
import {
  VALID_PATH_REGEX,
  folderExistsMessage,
  pathAvailableValidation,
} from '../../../../dev-center/components/validations/common.js';
import type { ExtensionDef } from '../../generate-schema/types.js';
import { defineField } from '../../generate-schema/definitions.js';
import { enrichWithDerivedFolder } from '../../utils/derive-folder.js';
import { generateDashboardMenuPlugin } from './generate.js';
import { DashboardMenuPluginAction } from './DashboardMenuPluginWizard.js';

const actionSchema = z
  .union([
    z
      .object({
        pageId: z
          .string()
          .uuid()
          .describe(defineField({ title: 'Page ID' })),
      })
      .describe(JSON.stringify({ title: 'Navigate to a page' })),
    z
      .object({
        modalId: z
          .string()
          .uuid()
          .describe(defineField({ title: 'Modal ID' })),
      })
      .describe(JSON.stringify({ title: 'Open a modal' })),
  ])
  .describe(
    defineField({
      title: 'Action',
      description:
        'The action performed when the menu item is clicked. Provide either pageId to navigate to a dashboard page or modalId to open a dashboard modal.',
    })
  );

function buildSlotIdField(containers: BackOfficeExtensionContainer[]) {
  if (containers.length === 0) {
    return z.string().describe(
      defineField({
        title: 'Slot to extend',
        description:
          'Component ID of the back-office menu extension container to extend. Obtain from the Dev Center.',
      })
    );
  }

  const literals = containers.map((c) =>
    z.literal(c.component.componentId).describe(
      defineField({
        title:
          c.component.data.backOfficeExtensionContainer.slotData?.displayName ??
          c.component.name,
      })
    )
  );

  const field =
    literals.length === 1
      ? literals[0]!
      : z.union(
          literals as [
            z.ZodLiteral<string>,
            z.ZodLiteral<string>,
            ...Array<z.ZodLiteral<string>>,
          ]
        );

  return field.describe(
    defineField({
      title: 'Slot to extend',
      description:
        'Component ID of the back-office menu extension container to extend.',
    })
  );
}

export const dashboardMenuPlugin: ExtensionDef = {
  title: 'Dashboard Menu Plugin',
  description:
    'A plugin that adds a menu item to a back-office page navigation',
  group: 'Dashboard',
  documentation:
    'https://dev.wix.com/docs/wix-cli/guides/extensions/dashboard-extensions/dashboard-menu-plugins/add-dashboard-menu-plugin-extensions',
  zodSchema: async (httpClient) => {
    const client = new ExtensionDataClient(httpClient);
    const containers = await client.getBackofficeMenuItems();
    return z.object({
      extensionType: z.literal('DASHBOARD_MENU_PLUGIN'),
      extendsSlotId: buildSlotIdField(containers),
      action: actionSchema,
      title: z.string().describe(defineField({ title: 'Menu item title' })),
      folder: z
        .string()
        .regex(VALID_PATH_REGEX)
        .optional()
        .describe(
          defineField({
            title: 'Plugin folder',
            description:
              'Lowercase alphanumeric and hyphens only. Must be unique within src/dashboard/menu-plugins/. Derived from the menu item title when omitted.',
          })
        ),
    });
  },
  enrich: (data) => enrichWithDerivedFolder(data, { sourceField: 'title' }),
  validate: async (projectFolder, data) => {
    const isAvailable = pathAvailableValidation(
      getDashboardMenuPluginExtensionsDir(projectFolder)
    )(data.folder);
    return isAvailable
      ? null
      : folderExistsMessage('menu plugin', data.folder, 'title');
  },
  generate: ({ projectFolder, data }) => {
    const actionType =
      'pageId' in data.action
        ? DashboardMenuPluginAction.NavigateToPage()
        : DashboardMenuPluginAction.OpenModal();

    const action: ActionSchema =
      'pageId' in data.action
        ? { navigateToPage: { pageId: data.action.pageId } }
        : { openModal: { componentId: data.action.modalId } };

    return generateDashboardMenuPlugin(
      projectFolder,
      data.extendsSlotId,
      actionType,
      action,
      data.folder,
      data.title
    );
  },
};
